/**
 * מקורות נתונים — the connections, their state, and how old their data is. 07 §2.3, §6.2, N-5, DG-2.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * "לא הוגדר" IS NOT A KIND OF "תקלה", AND "נמשך היום" IS NOT "עדכני".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Two collapses this screen refuses, both of them measured rather than imagined.
 *
 * ONE — NEVER CONFIGURED vs BROKEN. 07 N-5: a missing `data_source_type` silently became a working
 * default on 5 of 313 companies, in two independent code paths. Those five behaved exactly like five
 * configured companies, and nothing on any screen said otherwise. So `לא הוגדר` has its own chip,
 * its own sentence, its own next step — and its freshness line says *"לא התקבלו נתונים מהחיבור הזה
 * מעולם"* rather than showing a date, because there is no date and inventing one is the defect.
 *
 * TWO — THE TWO WATERMARKS. 07 §6.2 keeps `dataAsOfUtc` (the latest business date actually present)
 * apart from `fetchedAtUtc` (when we last successfully talked to the source), because *"the file
 * mtime moves on any rewrite, including a zero-row pull — the mechanism that let the June 2026
 * canonical freeze stay invisible."* A successful pull that returns nothing advances the fetch time
 * and MUST NOT advance the data time. The headline on every row is therefore the DATA watermark, and
 * the fetch time is shown beneath it, labelled as what it is. `con_fixture_0003` in the fixtures is
 * exactly that shape: fetched this morning, data three months old, and the row says so.
 *
 * THREE — AND `UNKNOWN` IS NOT `FRESH`. With no schedule and no SLA there is no basis for a verdict,
 * and the state is `UNKNOWN`. An unmeasurable SLA reported as healthy is a state that existed and
 * reached nobody.
 *
 * ── ORIGIN IS ON EVERY ROW ──────────────────────────────────────────────────────────────────────
 *
 * `live_source` vs `development_fixture` is the ONE branch a module is invited to make, and it is
 * the specific defence against a demo surface being mistaken for a proven integration. Every row
 * carries it, so a screen backed by a fake adapter says so per connection — not only through the
 * ribbon at the top of the page.
 *
 * ── AND NO HOST APPEARS ANYWHERE ────────────────────────────────────────────────────────────────
 *
 * 07 §2.3: an endpoint reference is a LOGICAL name resolved from deployment configuration — *"not a
 * connection string, not a host literal"* — and 07 N-6 forbids an adapter to hold a credential value
 * at all. There is no column here that could carry either, and the missing-configuration list names
 * the FIELDS that are absent, never their values.
 */

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { connectorStatusHe } from '../api/adminVocabulary';
import {
  completenessTextFor,
  freshnessTextFor,
  missingConfigurationHe,
  sourceStatusTextFor,
} from '../api/sourceStatusText';
import { fixtureSourceConnections, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';

const TITLE_HE = 'מקורות נתונים';

export function SourceConnectionsScreen(): JSX.Element {
  const ctx = useWorkContext();

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  const connections = fixturesEnabled() ? fixtureSourceConnections : [];
  const back = { labelHe: 'ניהול המשרד', href: routes.tenantAdmin };

  if (connections.length === 0) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="לא הוגדרו מקורות נתונים"
          bodyHe="המשרד עדיין לא חיבר אף מערכת חיצונית, ולכן אין נתונים שמגיעים מבחוץ."
          tone="empty"
          nextStep={<p>הגדרת חיבור נעשית מול התמיכה, ודורשת יעד לוגי והפניה לסוד הזדהות.</p>}
        />
      </AppShell>
    );
  }

  const neverConfigured = connections.filter((c) => c.state === 'NOT_CONFIGURED').length;
  const failing = connections.filter((c) => c.state === 'ERROR').length;

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={back}
      provenanceHe="״נתונים נכון ל־״ הוא התאריך העסקי האחרון שקיים בנתונים — לא מועד הפנייה האחרונה למקור. פנייה שהחזירה כלום מקדמת את מועד הפנייה בלבד."
    >
      {/* 09 §5.1's rule about figures: the headline says which N it is, in words. */}
      <p className="summary-line">
        {`${connections.length} חיבורים · ${neverConfigured} טרם הוגדרו · ${failing} בתקלה`}
      </p>

      <section className="connections">
        {connections.map((connection) => {
          const status = sourceStatusTextFor(connection);
          const freshness = freshnessTextFor(connection.freshness);
          const completeness = completenessTextFor(connection.completeness);
          const connector = connectorStatusHe(connection.connectorStatus);
          const missing = missingConfigurationHe(connection.missingConfiguration);

          return (
            <article
              key={connection.connectionUid}
              className="connection"
              data-state={connection.state}
            >
              <header className="connection__head">
                <h2 className="connection__title">{connection.displayNameHe}</h2>
                <StatusChip labelHe={status.labelHe} tone={status.tone} />
                <StatusChip labelHe={connector.labelHe} tone={connector.tone} />
                {connection.origin === 'development_fixture' ? (
                  // Per connection, not only in the page ribbon. A single fake row in a list of real
                  // ones is the case the ribbon cannot describe.
                  <StatusChip labelHe="מקור פיתוח — אינו מערכת אמת" tone="warn" />
                ) : null}
              </header>

              <p className="connection__explanation">{status.explanationHe}</p>

              {missing === '' ? null : (
                // EVERY absent field, not the first. An operator fixing one at a time runs the probe
                // five times (07 N-5).
                <p className="connection__missing">{missing}</p>
              )}

              <dl className="connection__facts">
                <div className="connection__fact">
                  <dt>טריות הנתונים</dt>
                  <dd>
                    <StatusChip labelHe={freshness.labelHe} tone={freshness.tone} />
                    {/* The DATA watermark. This is the sentence the office reads. */}
                    <span className="connection__as-of">{freshness.asOfHe}</span>
                    {freshness.fetchedHe === null ? null : (
                      // The FETCH watermark, labelled, beneath. Never the headline.
                      <span className="connection__fetched">{freshness.fetchedHe}</span>
                    )}
                  </dd>
                </div>

                <div className="connection__fact">
                  <dt>שלמות הנתונים</dt>
                  <dd>
                    <StatusChip labelHe={completeness.labelHe} tone={completeness.tone} />
                    <span className="connection__as-of">{completeness.explanationHe}</span>
                    {completeness.totalRequiresQualification ? (
                      // 07 DG-2: a total is never rendered over an incomplete set without saying so.
                      <span className="connection__qualified">
                        סכומים המחושבים מהנתונים האלה יוצגו עם הסתייגות.
                      </span>
                    ) : null}
                  </dd>
                </div>

                <div className="connection__fact">
                  <dt>חברות מקושרות</dt>
                  <dd>
                    <span dir="ltr">{connection.boundCompanyCount}</span>
                  </dd>
                </div>
              </dl>

              <p className="connection__next-step">{status.nextStepHe}</p>

              {/*
                THE NEXT STEP IS NOW REACHABLE, WHICH IT WAS NOT.
                Every sentence above ends in an instruction — "השלמת פרטי החיבור", "הרצת בדיקת חיבור"
                — and until this link existed there was nowhere to carry any of them out. A written
                next step with no address is the G-03 pattern in one line: correct, documented, and
                reaching nothing.
              */}
              <div className="actions-row">
                <a className="btn" href={routes.tenantSourceConnection(connection.connectionUid)}>
                  פתיחת החיבור — הגדרה, בדיקה וקישור חברות
                </a>
              </div>
            </article>
          );
        })}
      </section>
    </AppShell>
  );
}
